import api from '@shared/services/api';
import { structuralGroupService } from '@modules/organizationalStructure/structuralGroups/services/structuralGroups';
import { createReportConfig } from '@utils/reportHelper';

const AGE_REPORT_KEY = 'employees-retirement-age';
const TERM_CONTRACTS_KEY = 'employees-term-contracts';
const PERMANENT_CONTRACTS_KEY = 'employees-permanent-contracts';
const WOMAN_SHARE_REPORT_KEY = 'ratio-women-personnel';
const EMP_COUNT_REPORT_KEY = 'number-of-employees';
const AVERAGE_MONTHLY_SALARY_REPORT_KEY = 'average-monthly-salary';
const EMPLOYEE_LIST_REPORT_KEY = 'employee-list';
const EMPLOYEE_LENGTH_OF_SERVICE_REPORT_KEY = 'employee-length-of-service';
const TERMINATED_EMPLOYEES_REPORT_KEY = 'terminated-employees';
const APPOINTED_EMPLOYEES_REPORT_KEY = 'appointed-employees';
const REDUCED_WORKING_CAPACITY_REPORT_KEY = 'reduced-working-capacity';
const PERSONNEL_TABLE_REPORT_KEY = 'personnel-table';
const JOB_TABLE_REPORT_KEY = 'job-table';
const PERSONNEL_MOVEMENT_REPORT_KEY = 'personnel-movement';
const FORM_NSI_REPORT_KEY = 'form-nsi';
const STRUCTURAL_GROUP_HISTORY_REPORT_KEY = 'structural-group-history';
const COST_CENTER_HISTORY_REPORT_KEY = 'cost-center-history';
const POSITION_OCCUPANTS_HISTORY_REPORT_KEY = 'position-occupants-history';
const RIGHTS_OF_POSITION_REPORT_KEY = 'rights-of-position';
const POSITIONS_HELD_HISTORY_REPORT_KEY = 'positions-held-history';
const EMPLOYEES_INSURANCE_TYPE_REPORT_KEY = 'employees-insurance-type';
const EMPLOYEE_CHILDREN_UNDER_19_REPORT_KEY = 'employee-children-under-19';
const DC_SALARY_CHANGE_FROM_FILE_REPORT_KEY = 'dc-salary-change-from-file';
const EMPLOYEE_BANK_ACCOUNTS_REPORT_KEY = 'employee-bank-accounts-as-of-date';

const NSI_METRIC_ROWS = [
    {
        label: 'Брой на персонала към 01.01 на съответната година:',
        malePrev: 'previousMaleStart',
        maleCurr: 'maleStart',
        femalePrev: 'previousFemaleStart',
        femaleCurr: 'femaleStart',
    },
    {
        label: 'Доброволно или по взаимно съгласие напуснали през годината:',
        malePrev: 'previousMaleMutualTermination',
        maleCurr: 'maleMutualTermination',
        femalePrev: 'previousFemaleMutualTermination',
        femaleCurr: 'femaleMutualTermination',
    },
    {
        label: 'Уволнени:',
        malePrev: 'previousMaleDismissed',
        maleCurr: 'maleDismissed',
        femalePrev: 'previousFemaleDismissed',
        femaleCurr: 'femaleDismissed',
    },
    {
        label: 'Съкратени по чл.328 или освободени по чл.331 КТ:',
        malePrev: 'previousMaleRedundant',
        maleCurr: 'maleRedundant',
        femalePrev: 'previousFemaleRedundant',
        femaleCurr: 'femaleRedundant',
    },
    {
        label: 'Починали:',
        malePrev: 'previousMaleDead',
        maleCurr: 'maleDead',
        femalePrev: 'previousFemaleDead',
        femaleCurr: 'femaleDead',
    },
    {
        label: 'Пенсионирани по болест:',
        malePrev: 'previousMaleRetiredMedical',
        maleCurr: 'maleRetiredMedical',
        femalePrev: 'previousFemaleRetiredMedical',
        femaleCurr: 'femaleRetiredMedical',
    },
    {
        label: 'Пенсионирани по възраст и стаж:',
        malePrev: 'previousMaleRetiredAge',
        maleCurr: 'maleRetiredAge',
        femalePrev: 'previousFemaleRetiredAge',
        femaleCurr: 'femaleRetiredAge',
    },
    {
        label: 'Новоназначени през годината:',
        malePrev: 'previousMaleHired',
        maleCurr: 'maleHired',
        femalePrev: 'previousFemaleHired',
        femaleCurr: 'femaleHired',
    },
    {
        label: 'Брой на персонала към 31.12 на съответната година:',
        malePrev: 'previousMaleEnd',
        maleCurr: 'maleEnd',
        femalePrev: 'previousFemaleEnd',
        femaleCurr: 'femaleEnd',
    },
];

const CONTRACT_TYPE_TERM = 'TERM';
const CONTRACT_TYPE_PERMANENT = 'PERMANENT';

/** Same API/body/response; only contractType differs between report options */
const CONTRACT_TYPE_BY_REPORT_KEY = {
    [TERM_CONTRACTS_KEY]: CONTRACT_TYPE_TERM,
    [PERMANENT_CONTRACTS_KEY]: CONTRACT_TYPE_PERMANENT,
};

function pad(num) {
    return String(num).padStart(2, '0');
}

function formatDateForApi(value) {
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) {
        throw new Error('Invalid "For Date" value.');
    }
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function formatDateBg(value) {
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()}`;
}

function toOptionalInt(value) {
    if (value === '' || value == null) return undefined;
    const parsed = Number.parseInt(value, 10);
    return Number.isNaN(parsed) ? undefined : parsed;
}

function resolveGender(payload) {
    const hasMale = payload.maleYears != null || payload.maleMonths != null;
    const hasFemale = payload.femaleYears != null || payload.femaleMonths != null;

    if (hasMale && hasFemale) return 'BOTH';
    if (hasMale) return 'MALE';
    if (hasFemale) return 'FEMALE';

    throw new Error('Please fill age for Male, Female, or both.');
}

function buildAgeReportPayload(formState) {
    const payload = {
        asOfDate: formatDateForApi(formState.forDate),
        maleYears: toOptionalInt(formState.maleYears),
        maleMonths: toOptionalInt(formState.maleMonths),
        femaleYears: toOptionalInt(formState.femaleYears),
        femaleMonths: toOptionalInt(formState.femaleMonths),
        orgUnitId: formState.selectedOrgUnitId || undefined,
    };

    payload.gender = resolveGender(payload);
    return payload;
}

function buildAgeCriteriaLines(payload) {
    const lines = [];
    if (payload.maleYears != null || payload.maleMonths != null) {
        lines.push(`За Мъже - ${payload.maleYears ?? 0} години, ${payload.maleMonths ?? 0} месец/а`);
    }
    if (payload.femaleYears != null || payload.femaleMonths != null) {
        lines.push(`За Жени - ${payload.femaleYears ?? 0} години, ${payload.femaleMonths ?? 0} месец/а`);
    }
    return lines;
}

function createAgeReportPdfConfig(asOfDate, payload) {
    const dateText = formatDateBg(asOfDate || new Date());

    return createReportConfig(
        `Списък на служителите навършили възраст/в години, месеци/ към ${dateText}`,
        [
            { key: 'fullName', label: 'Име, Презиме, Фамилия' },
            {
                key: 'gender',
                label: 'Пол',
                render: (item) => {
                    if (item?.gender === 'MALE') return 'Мъж';
                    if (item?.gender === 'FEMALE') return 'Жена';
                    return item?.gender || '-';
                },
            },
            { key: 'position', label: 'Длъжност' },
            { key: 'orgUnit', label: 'Структурна група' },
            {
                key: 'dateOfBirth',
                label: 'Дата на раждане',
                render: (item) => formatDateBg(item?.dateOfBirth),
            },
            {
                label: `Възраст към ${dateText}`,
                children: [
                    { key: 'ageYears', label: 'години' },
                    { key: 'ageMonths', label: 'месеци' },
                ],
            },
            {
                label: `Трудов стаж към ${dateText}`,
                children: [
                    {
                        key: 'serviceYears',
                        label: 'години',
                        render: (item) => item?.serviceYears ?? '-',
                    },
                    {
                        key: 'serviceMonths',
                        label: 'месеци',
                        render: (item) => item?.serviceMonths ?? '-',
                    },
                ],
            },
        ],
        'employees-retirement-age-report',
        '',
        {
            rowNumberLabel: 'Сл.№',
        },
    );
}

function createAgeReportConfigWithCriteria(asOfDate, payload) {
    const baseConfig = createAgeReportPdfConfig(asOfDate, payload);
    return {
        ...baseConfig,
        preTableLines: buildAgeCriteriaLines(payload),
    };
}

async function getAgeReport(payload) {
    const response = await api.post('/emp-report/age', payload);
    return response.data;
}

function formatGenderLabel(gender) {
    if (gender === 'MALE') return 'Мъж';
    if (gender === 'FEMALE') return 'Жена';
    return gender || '-';
}

function formatContractExpiry(item) {
    const raw =
        item?.contractEndDate
        ?? item?.contractExpiresOn
        ?? item?.endDate
        ?? item?.expirationDate
        ?? item?.dateContractEnd
        ?? item?.empContractEndDate;
    return formatDateBg(raw) || '-';
}

function applyOptionalOrgUnitId(payload, formState) {
    const orgLabel = String(formState?.selectedOrgUnitLabel || '').trim();
    if (
        orgLabel
        && formState?.selectedOrgUnitId != null
        && formState?.selectedOrgUnitId !== ''
    ) {
        payload.orgUnitId = formState.selectedOrgUnitId;
    }
    return payload;
}

function buildContractTypeReportPayload(formState, contractType) {
    return applyOptionalOrgUnitId(
        {
            asOfDate: formatDateForApi(formState.forDate),
            contractType,
        },
        formState,
    );
}

function buildDateOrgUnitReportPayload(formState) {
    return applyOptionalOrgUnitId(
        { asOfDate: formatDateForApi(formState.forDate) },
        formState,
    );
}

function buildPeriodOrgUnitReportPayload(formState) {
    const payload = applyOptionalOrgUnitId(
        {
            fromDate: formatDateForApi(formState.fromDate),
            toDate: formatDateForApi(formState.toDate),
        },
        formState,
    );
    if (formState?.orderedBy) {
        payload.orderedBy = formState.orderedBy;
    }
    return payload;
}

function buildRequiredOrgUnitPayload(formState) {
    const orgLabel = String(formState?.selectedOrgUnitLabel || '').trim();
    const orgUnitId = formState?.selectedOrgUnitId;
    if (!orgLabel || orgUnitId == null || orgUnitId === '') {
        throw new Error('Please select an organizational unit.');
    }
    return { orgUnitId };
}

function buildRequiredPositionPayload(formState) {
    const positionLabel = String(formState?.selectedPositionLabel || '').trim();
    const positionId = formState?.selectedPositionId;
    if (!positionLabel || positionId == null || positionId === '') {
        throw new Error('Please select a position.');
    }
    return { positionId };
}

function buildPositionRightsReportPayload(formState) {
    const positionLabel = String(formState?.selectedPositionLabel || '').trim();
    const positionId = formState?.selectedPositionId;
    if (!positionLabel || positionId == null || positionId === '') {
        throw new Error('Please select a position.');
    }
    if (!formState?.forDate) {
        throw new Error('Please select a date.');
    }
    return {
        asOfDate: formatDateForApi(formState.forDate),
        positionId,
    };
}

function formatDecimalBg(value, fractionDigits = 2) {
    if (value == null || value === '') return '-';
    const num = Number(value);
    if (Number.isNaN(num)) return String(value);
    return num.toLocaleString('bg-BG', {
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
    });
}

function formatWomenPercentage(value) {
    return formatDecimalBg(value, 2);
}

function toSingleRowReportData(data) {
    if (Array.isArray(data)) return data;
    if (data != null && typeof data === 'object') return [data];
    return [];
}

function dash(value) {
    if (value == null || value === '') return '-';
    return String(value);
}

function formatBankAccStatus(value) {
    if (value === true || value === 'true') return 'Да';
    if (value === false || value === 'false') return 'Не';
    return dash(value);
}

function pickField(item, ...keys) {
    for (const key of keys) {
        const value = item?.[key];
        if (value != null && value !== '') return value;
    }
    return null;
}

function getProfExpParts(item) {
    const raw = item?.profExp;
    if (raw == null || raw === '') {
        return { tableNo: '-', percent: '-', amount: '-' };
    }
    if (typeof raw === 'object') {
        return {
            tableNo: dash(pickField(raw, 'tableNo', 'tableNumber', 'number')),
            percent: dash(pickField(raw, 'percent', 'percentage')),
            amount: dash(pickField(raw, 'amount', 'sum')),
        };
    }
    return { tableNo: dash(raw), percent: '-', amount: '-' };
}

function buildEmpListReportPayload(formState) {
    const payload = applyOptionalOrgUnitId(
        { asOfDate: formatDateForApi(formState.forDate) },
        formState,
    );
    if (formState?.orderedBy) {
        payload.orderedBy = formState.orderedBy;
    }
    return payload;
}

function mapServiceStatus(value) {
    if (value === 'terminated') return 'TERMINATED';
    return 'ACTIVE';
}

function buildEmpExpReportPayload(formState) {
    const payload = applyOptionalOrgUnitId(
        { asOfDate: formatDateForApi(formState.forDate) },
        formState,
    );
    payload.status = mapServiceStatus(formState?.lengthOfServiceStatus);
    return payload;
}

function normalizeToArray(data) {
    if (Array.isArray(data)) return data;
    if (data != null && typeof data === 'object') return [data];
    return [];
}

function getServiceValue(item, key) {
    const value = item?.service?.[key];
    if (value == null || value === '') return '-';
    return String(value);
}

function createEmpExpPdfConfig(asOfDate) {
    const dateText = formatDateBg(asOfDate || new Date());
    return createReportConfig(
        `Трудов стаж на служителите към ${dateText}`,
        [
            { key: 'empId', label: 'Сл.номер' },
            { key: 'positionName', label: 'Длъжност' },
            { key: 'fullName', label: 'Име Презиме Фамилия' },
            {
                key: 'effectiveDate',
                label: 'Дата на постъпване',
                render: (item) => formatDateBg(item?.effectiveDate) || '-',
            },
            { key: 'contractNoDate', label: 'Трудов договор номер/дата' },
            { key: 'contractType', label: 'Вид трудов договор' },
            {
                label: 'Общ трудов стаж',
                children: [
                    { key: 'kind0Years', label: 'години', render: (item) => getServiceValue(item, 'kind0Years') },
                    { key: 'kind0Months', label: 'месеци', render: (item) => getServiceValue(item, 'kind0Months') },
                    { key: 'kind0Days', label: 'дни', render: (item) => getServiceValue(item, 'kind0Days') },
                ],
            },
            {
                label: 'Трудов стаж във фирмата',
                children: [
                    { key: 'kind1Years', label: 'години', render: (item) => getServiceValue(item, 'kind1Years') },
                    { key: 'kind1Months', label: 'месеци', render: (item) => getServiceValue(item, 'kind1Months') },
                    { key: 'kind1Days', label: 'дни', render: (item) => getServiceValue(item, 'kind1Days') },
                ],
            },
            {
                label: 'Професионален стаж',
                children: [
                    { key: 'kind2Years', label: 'години', render: (item) => getServiceValue(item, 'kind2Years') },
                    { key: 'kind2Months', label: 'месеци', render: (item) => getServiceValue(item, 'kind2Months') },
                    { key: 'kind2Days', label: 'дни', render: (item) => getServiceValue(item, 'kind2Days') },
                ],
            },
        ],
        'employee-length-of-service-report',
        '',
        { rowNumberLabel: '№ ред' },
    );
}

function createTerminatedEmployeesPdfConfig(fromDate, toDate) {
    const fromText = formatDateBg(fromDate || new Date());
    const toText = formatDateBg(toDate || new Date());
    const baseConfig = createReportConfig(
        `Списък на служители с прекратено трудово правоотношение за периода от ${fromText} до ${toText}`,
        [
            { key: 'empId', label: 'Сл.№' },
            { key: 'idBdsk', label: 'ID номер', render: (item) => dash(item?.idBdsk) },
            { key: 'fullName', label: 'Име Презиме Фамилия' },
            { key: 'idn', label: 'ЕГН/ЛНЧ', render: (item) => dash(item?.idn) },
            {
                key: 'dateOfTermination',
                label: 'Дата на напускане',
                render: (item) => formatDateBg(item?.dateOfTermination) || '-',
            },
            {
                label: 'Ниво в структурата',
                children: [
                    { key: 'direction', label: 'Направление', render: (item) => dash(pickField(item, 'direction', 'directionName')) },
                    { key: 'levelName', label: 'Наименование', render: (item) => dash(pickField(item, 'levelName', 'orgUnitLevelName')) },
                    { key: 'directorate', label: 'Дирекция', render: (item) => dash(pickField(item, 'directorate', 'directorateName')) },
                    { key: 'department', label: 'Отдел', render: (item) => dash(pickField(item, 'department', 'departmentName')) },
                    { key: 'unit', label: 'Звено', render: (item) => dash(pickField(item, 'unit', 'unitName', 'zveno')) },
                    { key: 'group', label: 'Група', render: (item) => dash(pickField(item, 'group', 'groupName', 'grupa')) },
                ],
            },
            { key: 'orgUnitLevelName', label: 'Принадлежност към', render: (item) => dash(item?.orgUnitLevelName) },
            { key: 'descriptionForTermination', label: 'Описание по КТ за напускане', render: (item) => dash(item?.descriptionForTermination) },
            { key: 'reasonForTermination', label: 'Основание за напускане', render: (item) => dash(item?.reasonForTermination) },
            { key: 'effectiveDate', label: 'Дата на постъпване', render: (item) => formatDateBg(item?.effectiveDate) || '-' },
            { key: 'typeOfContract', label: 'Тр.договор', render: (item) => dash(item?.typeOfContract) },
            { key: 'orgUnitName', label: 'Структурна група', render: (item) => dash(item?.orgUnitName) },
            { key: 'positionName', label: 'Длъжност', render: (item) => dash(item?.positionName) },
            { key: 'education', label: 'Образование', render: (item) => dash(item?.education) },
            {
                label: 'Общ трудов стаж',
                children: [
                    { key: 'kind0Years', label: 'години', render: (item) => getServiceValue(item, 'kind0Years') },
                    { key: 'kind0Months', label: 'месеци', render: (item) => getServiceValue(item, 'kind0Months') },
                    { key: 'kind0Days', label: 'дни', render: (item) => getServiceValue(item, 'kind0Days') },
                ],
            },
            {
                label: 'Трудов стаж в компанията',
                children: [
                    { key: 'kind1Years', label: 'години', render: (item) => getServiceValue(item, 'kind1Years') },
                    { key: 'kind1Months', label: 'месеци', render: (item) => getServiceValue(item, 'kind1Months') },
                    { key: 'kind1Days', label: 'дни', render: (item) => getServiceValue(item, 'kind1Days') },
                ],
            },
            { key: 'salary', label: 'Основна месечна заплата (евро)', render: (item) => dash(item?.salary) },
            {
                key: 'profExpAmount',
                label: 'Сума за професионален опит (евро)',
                render: (item) => dash(pickField(item, 'profExpAmount', 'professionalExperienceAmount', 'expAmount', 'serviceAmount')),
            },
        ],
        'terminated-employees-report',
        '',
        {
            rowNumberLabel: '№ ред',
            tableFontSize: '5',
            titleFontSize: '11',
            pageOrientation: 'landscape',
        },
    );

    return {
        ...baseConfig,
        compactTable: true,
    };
}

function createAppointedEmployeesPdfConfig(fromDate, toDate) {
    const fromText = formatDateBg(fromDate || new Date());
    const toText = formatDateBg(toDate || new Date());
    const baseConfig = createReportConfig(
        `Списък на постъпилите служители за периода от ${fromText} до ${toText}`,
        [
            { key: 'empId', label: 'Сл.№' },
            { key: 'idBdsk', label: 'ID номер', render: (item) => dash(item?.idBdsk) },
            { key: 'fullName', label: 'Име Презиме Фамилия' },
            { key: 'idn', label: 'ЕГН/ЛНЧ', render: (item) => dash(item?.idn) },
            {
                key: 'effectiveDate',
                label: 'Дата на постъпване',
                render: (item) => formatDateBg(item?.effectiveDate) || '-',
            },
            {
                key: 'dateOfTermination',
                label: 'Дата на напускане',
                render: (item) => formatDateBg(item?.dateOfTermination) || '-',
            },
            {
                label: 'Ниво в структурата',
                children: [
                    { key: 'direction', label: 'Направление', render: (item) => dash(pickField(item, 'direction', 'directionName')) },
                    { key: 'levelName', label: 'Наименование', render: (item) => dash(pickField(item, 'levelName', 'orgUnitLevelName')) },
                    { key: 'directorate', label: 'Дирекция', render: (item) => dash(pickField(item, 'directorate', 'directorateName')) },
                    { key: 'department', label: 'Отдел', render: (item) => dash(pickField(item, 'department', 'departmentName')) },
                    { key: 'unit', label: 'Звено', render: (item) => dash(pickField(item, 'unit', 'unitName', 'zveno')) },
                    { key: 'group', label: 'Група', render: (item) => dash(pickField(item, 'group', 'groupName', 'grupa')) },
                ],
            },
            { key: 'orgUnitLevelName', label: 'Принадлежност към', render: (item) => dash(item?.orgUnitLevelName) },
            {
                label: 'Трудов договор',
                children: [
                    { key: 'typeOfContract', label: 'вид', render: (item) => dash(item?.typeOfContract) },
                    { key: 'reasonCode', label: 'код основание', render: (item) => dash(item?.reasonCode) },
                    { key: 'reasonName', label: 'наименование на основание', render: (item) => dash(item?.reasonName) },
                ],
            },
            { key: 'orgUnitName', label: 'Структурна група', render: (item) => dash(item?.orgUnitName) },
            {
                label: 'Разходен център',
                children: [
                    { key: 'empOfficeCode', label: 'код', render: (item) => dash(item?.empOfficeCode) },
                    { key: 'empOfficeName', label: 'наименование', render: (item) => dash(item?.empOfficeName) },
                ],
            },
            { key: 'positionName', label: 'Длъжност', render: (item) => dash(item?.positionName) },
            { key: 'education', label: 'Образование', render: (item) => dash(item?.education) },
            {
                label: 'Общ трудов стаж',
                children: [
                    { key: 'kind0Years', label: 'години', render: (item) => getServiceValue(item, 'kind0Years') },
                    { key: 'kind0Months', label: 'месеци', render: (item) => getServiceValue(item, 'kind0Months') },
                    { key: 'kind0Days', label: 'дни', render: (item) => getServiceValue(item, 'kind0Days') },
                ],
            },
            {
                label: 'Основна месечна заплата',
                children: [
                    { key: 'salary', label: 'сума', render: (item) => dash(item?.salary) },
                    { key: 'salaryCurrency', label: 'валута', render: () => 'EUR' },
                ],
            },
            { key: 'phone', label: 'Мобилен телефон', render: (item) => dash(pickField(item, 'mobilePhone', 'mobile', 'phone')) },
        ],
        'appointed-employees-report',
        '',
        {
            rowNumberLabel: '№ ред',
            tableFontSize: '5',
            titleFontSize: '11',
            pageOrientation: 'landscape',
        },
    );

    return {
        ...baseConfig,
        compactTable: true,
    };
}

function createReducedWorkingCapacityPdfConfig(fromDate, toDate) {
    const fromText = formatDateBg(fromDate || new Date());
    const toText = formatDateBg(toDate || new Date());

    return createReportConfig(
        `Списък на служителите с намалена работоспособност за периода от ${fromText} г. до ${toText} г.`,
        [
            { key: 'empId', label: 'Сл.№' },
            { key: 'fullName', label: 'Име Презиме Фамилия' },
            { key: 'idn', label: 'ЕГН/ЛНЧ', render: (item) => dash(item?.idn) },
            { key: 'orgUnitName', label: 'Структурна група', render: (item) => dash(item?.orgUnitName) },
            { key: 'positionName', label: 'Длъжност', render: (item) => dash(item?.positionName) },
            {
                key: 'fromDateDisability',
                label: 'Дата на настъпване на инвалидност',
                render: (item) => formatDateBg(item?.fromDateDisability) || '-',
            },
            {
                key: 'toDateDisability',
                label: 'Валидност на решение на ТЕЛК',
                render: (item) => formatDateBg(item?.toDateDisability) || '-',
            },
            {
                key: 'percentageDisability',
                label: '% инвалидност',
                render: (item) => formatDecimalBg(item?.percentageDisability, 2),
            },
            {
                key: 'typeDoc',
                label: 'Тип документ/Описание',
                render: (item) => dash(item?.typeDoc),
            },
        ],
        'reduced-working-capacity-report',
        '',
        { rowNumberLabel: '№ ред' },
    );
}

function formatOrgStructurePathTitle(path) {
    if (!path) return '>>';
    const parts = String(path).split('>>').map((part) => part.trim()).filter(Boolean);
    return `>>${parts.join('>>')}`;
}

function sortOrgStructurePaths(paths) {
    return [...paths].sort((left, right) => {
        const leftParts = String(left).split('>>').map((part) => part.trim());
        const rightParts = String(right).split('>>').map((part) => part.trim());
        const sharedLength = Math.min(leftParts.length, rightParts.length);

        for (let index = 0; index < sharedLength; index += 1) {
            const comparison = leftParts[index].localeCompare(rightParts[index], 'bg');
            if (comparison !== 0) return comparison;
        }

        return leftParts.length - rightParts.length;
    });
}

function formatNominalStaffPosition(position) {
    if (position == null || position === '') return '-';
    const raw = String(position);
    const match = raw.match(/^(\d+_\d+)\s*[-–]?\s*(.+)$/);
    if (match?.[2]) {
        return `${match[1]} - ${match[2].trim()}`;
    }

    const splitMatch = raw.match(/^(\d+_\d+)([A-Za-zА-Яа-я].*)$/);
    if (splitMatch?.[2]) {
        return `${splitMatch[1]} - ${splitMatch[2].trim()}`;
    }

    return raw;
}

function formatNominalStaffDecimal(value) {
    if (value == null || value === '') return '-';
    const num = Number(value);
    if (Number.isNaN(num)) return String(value);
    return num.toLocaleString('bg-BG', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
}

function createPersonnelTablePdfConfig(asOfDate) {
    const dateText = formatDateBg(asOfDate || new Date());

    const baseConfig = createReportConfig(
        'ПОИМЕННО ЩАТНО РАЗПИСАНИЕ',
        [
            { key: 'position', label: 'Длъжност', render: (item) => formatNominalStaffPosition(item?.position) },
            { key: 'fullName', label: 'Име Презиме Фамилия' },
            { key: 'education', label: 'Образование', render: (item) => dash(item?.education) },
            {
                label: 'Основна заплата',
                children: [
                    { key: 'salary', label: 'сума', render: (item) => formatNominalStaffDecimal(item?.salary) },
                    { key: 'salaryCurrency', label: 'валута', render: () => 'евро' },
                ],
            },
            { key: 'typeContract', label: 'Трудов договор', render: (item) => dash(item?.typeContract) },
            {
                key: 'durationDays',
                label: 'Продълж.раб.ден(часа)',
                render: (item) => formatNominalStaffDecimal(item?.durationDays),
            },
            {
                key: 'timeServed',
                label: 'Прослужено време(%)',
                render: (item) => {
                    if (item?.timeServed == null || item?.timeServed === '') return '-';
                    return formatNominalStaffDecimal(item.timeServed);
                },
            },
            { key: 'nkpd', label: 'Код по НКПД', render: (item) => dash(item?.nkpd) },
        ],
        'personnel-table-report',
        '',
        { rowNumberLabel: '№ ред' },
    );

    return {
        ...baseConfig,
        hidePageHeader: true,
        subtitle: `от ${dateText} г.`,
        preTableFontSize: 9,
        groupRowsBy: 'orgStructurePath',
        formatGroupTitle: formatOrgStructurePathTitle,
        sortGroupKeys: sortOrgStructurePaths,
        resetRowNumberPerGroup: true,
        preTableLines: [
            'Утвърждавам:. . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . .',
            '/Изпълнителен директор (IIIIIIIIIIIIIIIIIIII)/',
        ],
    };
}

function formatStaffCount(value) {
    if (value == null || value === '') return '-';
    const num = Number(value);
    if (Number.isNaN(num)) return String(value);
    return String(num);
}

function createJobTablePdfConfig(asOfDate) {
    const dateText = formatDateBg(asOfDate || new Date());

    const baseConfig = createReportConfig(
        'ЩАТНО РАЗПИСАНИЕ НА ДЛЪЖНОСТИТЕ',
        [
            { key: 'positionName', label: 'Длъжност', render: (item) => dash(item?.positionName) },
            { key: 'contractType', label: 'Трудов договор', render: (item) => dash(item?.contractType) },
            {
                label: 'Щатни бройки',
                children: [
                    { key: 'totalPositions', label: 'общо', render: (item) => formatStaffCount(item?.totalPositions) },
                    { key: 'occupiedPositions', label: 'заети', render: (item) => formatStaffCount(item?.occupiedPositions) },
                    { key: 'vacantPositions', label: 'незаети', render: (item) => formatStaffCount(item?.vacantPositions) },
                ],
            },
            {
                label: 'Основна заплата',
                children: [
                    { key: 'minSalary', label: 'от', render: (item) => formatNominalStaffDecimal(item?.minSalary) },
                    { key: 'maxSalary', label: 'до', render: (item) => formatNominalStaffDecimal(item?.maxSalary) },
                ],
            },
            {
                label: 'Брутна заплата',
                children: [
                    { key: 'minGrossSalary', label: 'от', render: (item) => formatNominalStaffDecimal(item?.minGrossSalary) },
                    { key: 'maxGrossSalary', label: 'до', render: (item) => formatNominalStaffDecimal(item?.maxGrossSalary) },
                ],
            },
            { key: 'nkpd', label: 'Код по НКПД', render: (item) => dash(item?.nkpd) },
        ],
        'job-table-report',
        '',
        { rowNumberLabel: '№ ред' },
    );

    return {
        ...baseConfig,
        hidePageHeader: true,
        subtitle: `от ${dateText} г.`,
        preTableFontSize: 9,
        groupRowsBy: 'orgStructurePath',
        formatGroupTitle: formatOrgStructurePathTitle,
        sortGroupKeys: sortOrgStructurePaths,
        resetRowNumberPerGroup: true,
        preTableLines: [
            'Утвърждавам:. . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . .',
            '/Изпълнителен директор (IIIIIIIIIIIIIIIIIIIIII)/',
        ],
    };
}

function createEmpListPdfConfig(asOfDate) {
    const dateText = formatDateBg(asOfDate || new Date());

    const columns = [
        { key: 'empId', label: 'Сл.№' },
        {
            key: 'idBdsk',
            label: 'ID номер',
            render: (item) => dash(item?.idBdsk),
        },
        { key: 'idn', label: 'ЕГН/ЛНЧ' },
        { key: 'fullName', label: 'Име Презиме Фамилия' },
        { key: 'nkpd', label: 'Код по НКПД' },
        {
            label: 'Длъжност',
            children: [
                { key: 'positionCode', label: 'код' },
                { key: 'positionName', label: 'наименование' },
            ],
        },
        {
            label: 'Локация',
            children: [
                {
                    key: 'cityName',
                    label: 'населено място',
                    render: (item) => dash(item?.cityName),
                },
                {
                    key: 'locationName',
                    label: 'наименование',
                    render: (item) => dash(item?.locationName),
                },
            ],
        },
        {
            key: 'orgUnitName',
            label: 'Структурна група',
            render: (item) => dash(item?.orgUnitName),
        },
        {
            label: 'Ниво в структурата',
            children: [
                {
                    key: 'direction',
                    label: 'Направление',
                    render: (item) => dash(pickField(item, 'direction', 'directionName')),
                },
                {
                    key: 'structureLevelName',
                    label: 'Наименование',
                    render: (item) => dash(pickField(item, 'structureLevelName', 'levelName', 'orgUnitLevelName')),
                },
                {
                    key: 'directorate',
                    label: 'Дирекция',
                    render: (item) => dash(pickField(item, 'directorate', 'directorateName')),
                },
                {
                    key: 'department',
                    label: 'Отдел',
                    render: (item) => dash(pickField(item, 'department', 'departmentName')),
                },
                {
                    key: 'division',
                    label: 'Звено',
                    render: (item) => dash(pickField(item, 'division', 'unit', 'unitName', 'zveno')),
                },
                {
                    key: 'group',
                    label: 'Група',
                    render: (item) => dash(pickField(item, 'group', 'groupName', 'grupa')),
                },
            ],
        },
        {
            key: 'unknow',
            label: 'Принадлежност',
            render: (item) => dash(pickField(item, 'unknow', 'unknown', 'affiliation', 'belonging')),
        },
        {
            label: 'Трудов договор',
            children: [
                {
                    key: 'contractType',
                    label: 'вид',
                    render: (item) => dash(item?.contractType),
                },
                {
                    key: 'contractCode',
                    label: 'код основание',
                    render: (item) => dash(item?.contractCode),
                },
                {
                    key: 'contractReason',
                    label: 'наименование на основание',
                    render: (item) => dash(item?.contractReason),
                },
            ],
        },
        {
            key: 'monthlyRewardBase',
            label: 'Формиране на месечно възнаграждение на база',
            render: (item) => dash(pickField(item, 'monthlyRewardBase', 'salaryFormationBase', 'rewardBase')),
        },
        {
            label: 'Основна месечна заплата',
            children: [
                {
                    key: 'salaryCurrency',
                    label: 'валута',
                    render: (item) => dash(pickField(item, 'salaryCurrency', 'currency') ?? 'EUR'),
                },
                {
                    key: 'salary',
                    label: 'сума',
                    render: (item) => dash(item?.salary),
                },
            ],
        },
        {
            label: 'Професионален опит',
            children: [
                {
                    key: 'profExpTableNo',
                    label: '№ таблица пр.опит',
                    render: (item) => getProfExpParts(item).tableNo,
                },
                {
                    key: 'profExpPercent',
                    label: 'процент',
                    render: (item) => getProfExpParts(item).percent,
                },
                {
                    key: 'profExpAmount',
                    label: 'сума',
                    render: (item) => getProfExpParts(item).amount,
                },
            ],
        },
        {
            key: 'phone',
            label: 'Телефон',
            render: (item) => dash(item?.phone),
        },
        {
            key: 'mobilePhone',
            label: 'Мобилен телефон',
            render: (item) => dash(pickField(item, 'mobilePhone', 'mobile', 'cellPhone')),
        },
        {
            key: 'email',
            label: 'Сл. E-Mail',
            render: (item) => dash(item?.email),
        },
        {
            key: 'noticePeriod',
            label: 'Срок на предизвестие при прекратяване на трудов договор',
            render: (item) => dash(item?.noticePeriod),
        },
    ];

    const baseConfig = createReportConfig(
        `Списък на служителите към ${dateText}`,
        columns,
        'employee-list-report',
        '',
        {
            rowNumberLabel: '№ ред',
            tableFontSize: '5',
            titleFontSize: '11',
            pageOrientation: 'landscape',
        },
    );

    return {
        ...baseConfig,
        compactTable: true,
    };
}

function formatMovementCount(value) {
    if (value == null || value === '') return '0';
    const num = Number(value);
    if (Number.isNaN(num)) return String(value);
    return String(num);
}

function formatNssiCell(value) {
    if (value == null || value === '') return '';
    const num = Number(value);
    if (Number.isNaN(num)) return String(value);
    if (num === 0) return '';
    return String(num);
}

function nssiHeaderCell(text, styles, options = {}) {
    return {
        text: String(text ?? ''),
        style: 'tableHeader',
        fillColor: styles.tableHeaderBgColor || '#28a745',
        color: styles.tableHeaderTextColor || '#ffffff',
        alignment: 'center',
        margin: [1, 2, 1, 2],
        ...options,
    };
}

function buildFormNsiCustomTable(data, styles, previousYear, currentYear) {
    const columnCount = 5;

    const headers = [
        [
            nssiHeaderCell('Възрастови групи', styles, { rowSpan: 2 }),
            nssiHeaderCell('Мъже', styles, { colSpan: 2 }),
            {},
            nssiHeaderCell('Жени', styles, { colSpan: 2 }),
            {},
        ],
        [
            {},
            nssiHeaderCell(String(previousYear), styles),
            nssiHeaderCell(String(currentYear), styles),
            nssiHeaderCell(String(previousYear), styles),
            nssiHeaderCell(String(currentYear), styles),
        ],
    ];

    const body = [];

    data.forEach((item) => {
        const ageGroup = item?.ageGroup ?? '';
        body.push([
            {
                text: `-------------------- ${ageGroup}`,
                colSpan: columnCount,
                alignment: 'left',
                bold: true,
                fillColor: styles.sectionHeaderBgColor || '#e9ecef',
            },
            ...Array.from({ length: columnCount - 1 }, () => ({})),
        ]);

        NSI_METRIC_ROWS.forEach((metric) => {
            body.push([
                { text: metric.label, alignment: 'left' },
                { text: formatNssiCell(item?.[metric.malePrev]), alignment: 'center' },
                { text: formatNssiCell(item?.[metric.maleCurr]), alignment: 'center' },
                { text: formatNssiCell(item?.[metric.femalePrev]), alignment: 'center' },
                { text: formatNssiCell(item?.[metric.femaleCurr]), alignment: 'center' },
            ]);
        });
    });

    return {
        headers,
        body,
        headerRows: headers.length,
        columnCount,
        widths: [200, 50, 50, 50, 50],
    };
}

function createStructuralGroupHistoryPdfConfig(fromDate, toDate) {
    const fromDateText = formatDateBg(fromDate || new Date());
    const toDateText = formatDateBg(toDate || new Date());

    const baseConfig = createReportConfig(
        `Справка за промяна на структурни единици от дата ${fromDateText} г. до дата ${toDateText} г.`,
        [
            {
                label: 'Структурна единица',
                children: [
                    { key: 'orgUnitCode', label: 'Код', render: (item) => dash(item?.orgUnitCode) },
                    { key: 'orgUnitName', label: 'Наименование', render: (item) => dash(item?.orgUnitName) },
                    { key: 'unitLevelName', label: 'Код ниво', render: (item) => dash(item?.unitLevelName) },
                    { key: 'location', label: 'Локация', render: (item) => dash(item?.location) },
                    { key: 'empOfficeName', label: 'Подчиненост', render: (item) => dash(item?.empOfficeName) },
                ],
            },
            {
                label: 'Старо състояние',
                children: [
                    {
                        key: 'oldParentOrgUnitCode',
                        label: 'Код пряк родител',
                        render: (item) => dash(item?.oldParentOrgUnitCode),
                    },
                    {
                        key: 'oldParentOrgUnitName',
                        label: 'Наименование на пряк родител',
                        render: (item) => dash(item?.oldParentOrgUnitName),
                    },
                ],
            },
            {
                label: 'Ново състояние',
                children: [
                    {
                        key: 'parentOrgUnitCode',
                        label: 'Код пряк родител',
                        render: (item) => dash(item?.parentOrgUnitCode),
                    },
                    {
                        key: 'parentOrgUnitName',
                        label: 'Наименование на пряк родител',
                        render: (item) => dash(item?.parentOrgUnitName),
                    },
                ],
            },
            {
                key: 'fromDate',
                label: 'От дата',
                render: (item) => formatDateBg(item?.fromDate) || '-',
            },
            {
                key: 'toDate',
                label: 'До дата',
                render: (item) => formatDateBg(item?.toDate) || '-',
            },
        ],
        'structural-group-history-report',
        '',
        {
            rowNumberLabel: '№ ред',
            tableFontSize: '4',
            titleFontSize: '9',
        },
    );

    return {
        ...baseConfig,
        compactTable: true,
        tableWidths: [30, 40, 105, 135, 70, 70, 100, 120, 135, 100, 120, 75],
    };
}

function createCostCenterHistoryPdfConfig(fromDate, toDate) {
    const fromDateText = formatDateBg(fromDate || new Date());
    const toDateText = formatDateBg(toDate || new Date());

    const baseConfig = createReportConfig(
        `Справка за история на промяна на разходен център от дата ${fromDateText} г. до дата ${toDateText} г.`,
        [
            {
                key: 'unitLevelName',
                label: 'Код ниво',
                render: (item) => dash(pickField(item, 'unitLevelName', 'uniteLevelName')),
            },
            {
                label: 'Структурна единица',
                children: [
                    { key: 'orgUnitCode', label: 'Код', render: (item) => dash(item?.orgUnitCode) },
                    { key: 'orgUnitName', label: 'Наименование', render: (item) => dash(item?.orgUnitName) },
                ],
            },
            {
                label: 'Позиция',
                children: [
                    { key: 'positionCode', label: 'Код', render: (item) => dash(item?.positionCode) },
                    { key: 'positionName', label: 'Наименование', render: (item) => dash(item?.positionName) },
                ],
            },
            {
                label: 'Стар разходен център',
                children: [
                    { key: 'oldCostCenterCode', label: 'Код', render: (item) => dash(item?.oldCostCenterCode) },
                    { key: 'oldCostCenterName', label: 'Наименование', render: (item) => dash(item?.oldCostCenterName) },
                ],
            },
            {
                label: 'Нов разходен център',
                children: [
                    { key: 'newCostCenterCode', label: 'Код', render: (item) => dash(item?.newCostCenterCode) },
                    { key: 'newCostCenterName', label: 'Наименование', render: (item) => dash(item?.newCostCenterName) },
                ],
            },
            {
                key: 'location',
                label: 'Локация',
                render: (item) => dash(item?.location),
            },
            {
                key: 'fromDate',
                label: 'От дата',
                render: (item) => formatDateBg(item?.fromDate) || '-',
            },
            {
                key: 'toDate',
                label: 'До дата',
                render: (item) => formatDateBg(item?.toDate) || '-',
            },
        ],
        'cost-center-history-report',
        '',
        {
            rowNumberLabel: '№ ред',
            tableFontSize: '8',
            titleFontSize: '11',
        },
    );

    return {
        ...baseConfig,
        compactTable: true,
    };
}

function normalizePositionRightKeys(rightKeys) {
    if (!Array.isArray(rightKeys)) return [];
    return [...rightKeys].sort((left, right) => Number(left.id) - Number(right.id));
}

function hasPositionRight(item, rightId) {
    const ids = Array.isArray(item?.rightKeyIds) ? item.rightKeyIds : [];
    return ids.some((id) => Number(id) === Number(rightId));
}

function createPositionRightsPdfConfig(asOfDate, rightKeys) {
    const dateText = formatDateBg(asOfDate || new Date());
    const sortedRights = normalizePositionRightKeys(rightKeys);

    const rightColumns = sortedRights.map((right) => ({
        key: `right_${right.id}`,
        label: right.rightDescription || right.approvalKey || String(right.id),
        render: (item) => (hasPositionRight(item, right.id) ? 'Да' : ''),
    }));

    const baseConfig = createReportConfig(
        `Права към ${dateText}`,
        [
            {
                label: 'Структурна единица',
                children: [
                    { key: 'orgUnitCode', label: 'Код', render: (item) => dash(item?.orgUnitCode) },
                    { key: 'orgUnitName', label: 'Наименование', render: (item) => dash(item?.orgUnitName) },
                ],
            },
            {
                label: 'Позиция',
                children: [
                    { key: 'positionCode', label: 'Код', render: (item) => dash(item?.positionCode) },
                    { key: 'positionName', label: 'Наименование', render: (item) => dash(item?.positionName) },
                ],
            },
            {
                key: 'ocupantName',
                label: 'Заемащ позицията',
                render: (item) => dash(pickField(item, 'ocupantName', 'occupantName')),
            },
            ...rightColumns,
            {
                key: 'fromDate',
                label: 'От дата',
                render: (item) => formatDateBg(item?.fromDate) || '-',
            },
            {
                key: 'toDate',
                label: 'До дата',
                render: (item) => formatDateBg(item?.toDate) || '-',
            },
        ],
        'rights-of-position-report',
        '',
        {
            rowNumberLabel: '№ ред',
            tableFontSize: '4',
            titleFontSize: '10',
            tableHeaderFontSize: '4',
            tableHeaderRowHeight: 22,
            tableHeaderLeafRowHeight: 36,
        },
    );

    return {
        ...baseConfig,
        compactTable: true,
    };
}

function formatInsuranceAmount(value) {
    if (value == null || value === '') return '-';
    const num = Number(value);
    if (Number.isNaN(num)) return String(value);
    return formatDecimalBg(num, 2);
}

function createEmployeesInsuranceTypePdfConfig(asOfDate) {
    const dateText = formatDateBg(asOfDate || new Date());

    const baseConfig = createReportConfig(
        `Справка - служители - вид осигуряване към ${dateText} г.`,
        [
            { key: 'idn', label: 'ЕГН/ЛНЧ', render: (item) => dash(item?.idn) },
            { key: 'empId', label: 'Сл.№', render: (item) => dash(item?.empId) },
            { key: 'fullName', label: 'Име Презиме Фамилия', render: (item) => dash(item?.fullName) },
            {
                label: 'Трудов договор',
                children: [
                    { key: 'typeContract', label: 'вид', render: (item) => dash(item?.typeContract) },
                    { key: 'reasonCode', label: 'код основание', render: (item) => dash(item?.reasonCode) },
                    { key: 'reason', label: 'наименование на основание', render: (item) => dash(item?.reason) },
                    {
                        key: 'breakDateContract',
                        label: 'дата на прекратяване',
                        render: (item) => formatDateBg(item?.breakDateContract) || '-',
                    },
                ],
            },
            {
                label: 'Вид осигурен',
                children: [
                    {
                        key: 'insuranceCode',
                        label: 'код по тр.договор',
                        render: (item) => dash(item?.insuranceCode),
                    },
                    {
                        label: 'осигурен при друг/външен/ работодател',
                        children: [
                            {
                                key: 'fromDate',
                                label: 'от дата',
                                render: (item) => formatDateBg(item?.fromDate) || '-',
                            },
                            {
                                key: 'sum',
                                label: 'сума',
                                render: (item) => formatInsuranceAmount(item?.sum),
                            },
                            {
                                key: 'insuranceCurrency',
                                label: 'валута',
                                render: (item) => dash(pickField(item, 'currency', 'insuranceCurrency', 'sumCurrency')),
                            },
                        ],
                    },
                    {
                        label: 'СИРВ',
                        children: [
                            { key: 'risvCode', label: 'код', render: (item) => dash(item?.risvCode) },
                        ],
                    },
                ],
            },
        ],
        'employees-insurance-type-report',
        '',
        {
            rowNumberLabel: '№ ред',
            tableFontSize: '6',
            titleFontSize: '11',
        },
    );

    return {
        ...baseConfig,
        compactTable: true,
    };
}

function formatChildCount(value) {
    if (value == null || value === '') return '-';
    const num = Number(value);
    if (Number.isNaN(num)) return String(value);
    return String(num);
}

function createEmployeeChildrenUnder19PdfConfig(asOfDate) {
    const dateText = formatDateBg(asOfDate || new Date());

    const baseConfig = createReportConfig(
        `Списък на децата на служители на възраст до 19 години към ${dateText} г.`,
        [
            { key: 'idn', label: 'ЕГН/ЛНЧ', render: (item) => dash(item?.idn) },
            { key: 'empId', label: 'Сл.№', render: (item) => dash(item?.empId) },
            { key: 'fullName', label: 'Име Презиме Фамилия', render: (item) => dash(item?.fullName) },
            { key: 'orgUnitName', label: 'Структурна група', render: (item) => dash(item?.orgUnitName) },
            {
                label: 'Дете',
                children: [
                    {
                        key: 'childNumber',
                        label: 'поредност',
                        render: (item) => formatChildCount(item?.childNumber),
                    },
                    {
                        key: 'childDateOfBirth',
                        label: 'дата на раждане',
                        render: (item) => formatDateBg(item?.childDateOfBirth) || '-',
                    },
                    { key: 'childIdn', label: 'ЕГН', render: (item) => dash(item?.childIdn) },
                    {
                        key: 'childName',
                        label: 'име презиме фамилия',
                        render: (item) => dash(item?.childName),
                    },
                    {
                        label: `навършени години към ${dateText} г.`,
                        children: [
                            {
                                key: 'childYears',
                                label: 'години',
                                render: (item) => formatChildCount(item?.childYears),
                            },
                            {
                                key: 'childMonths',
                                label: 'месеци',
                                render: (item) => formatChildCount(item?.childMonths),
                            },
                            {
                                key: 'childDays',
                                label: 'дни',
                                render: (item) => formatChildCount(item?.childDays),
                            },
                        ],
                    },
                    {
                        label: 'данъчно облекчение',
                        children: [
                            {
                                key: 'taxReductionYear',
                                label: 'за година',
                                render: (item) => formatChildCount(item?.taxReductionYear),
                            },
                            {
                                key: 'taxReductionType',
                                label: 'вид',
                                render: (item) => dash(item?.taxReductionType),
                            },
                            {
                                key: 'taxReductionPeriod',
                                label: 'период на изплащане на облекчението',
                                render: (item) => dash(item?.taxReductionPeriod),
                            },
                        ],
                    },
                ],
            },
        ],
        'employee-children-under-19-report',
        '',
        {
            rowNumberLabel: '№ ред',
            tableFontSize: '6',
            titleFontSize: '11',
        },
    );

    return {
        ...baseConfig,
        compactTable: true,
    };
}

function createDcSalaryChangeFromFilePdfConfig() {
    const baseConfig = createReportConfig(
        'Справка - Генерирани допълнителни споразумения по чл.119, ал.1 КТ или заповед по чл.118, ал.3 КТ за изменение на основното трудово възнаграждение',
        [
            { key: 'idn', label: 'ЕГН/ЛНЧ', render: (item) => dash(item?.idn) },
            { key: 'empId', label: 'Сл.№', render: (item) => dash(item?.empId) },
            { key: 'idBdsk', label: 'ID', render: (item) => dash(pickField(item, 'idBdsk', 'id')) },
            { key: 'fullName', label: 'Име, Презиме, Фамилия', render: (item) => dash(item?.fullName) },
            { key: 'typeContract', label: 'Тип тр.договор', render: (item) => dash(item?.typeContract) },
            { key: 'positionName', label: 'Длъжност', render: (item) => dash(item?.positionName) },
            {
                label: 'Ниво в структурата',
                children: [
                    {
                        key: 'direction',
                        label: 'Направление',
                        render: (item) => dash(pickField(item, 'direction', 'directionName')),
                    },
                    {
                        key: 'structureLevelName',
                        label: 'Наименование',
                        render: (item) => dash(pickField(item, 'structureLevelName', 'levelName', 'orgUnitLevelName')),
                    },
                    {
                        key: 'directorate',
                        label: 'Дирекция',
                        render: (item) => dash(pickField(item, 'directorate', 'directorateName')),
                    },
                    {
                        key: 'department',
                        label: 'Отдел',
                        render: (item) => dash(pickField(item, 'department', 'departmentName')),
                    },
                    {
                        key: 'division',
                        label: 'Звено',
                        render: (item) => dash(pickField(item, 'division', 'unit', 'unitName', 'zveno')),
                    },
                    {
                        key: 'group',
                        label: 'Група',
                        render: (item) => dash(pickField(item, 'group', 'groupName', 'grupa')),
                    },
                ],
            },
            {
                key: 'orgUnitName',
                label: 'Принадлежност към',
                render: (item) => dash(pickField(item, 'orgUnitName', 'orgUnitLevelName', 'affiliation')),
            },
            {
                label: 'Допълнително споразумение',
                children: [
                    { key: 'anexNumber', label: '№', render: (item) => dash(item?.anexNumber) },
                    {
                        key: 'anexFromDate',
                        label: 'от дата',
                        render: (item) => formatDateBg(item?.anexFromDate) || '-',
                    },
                    {
                        key: 'anexEffectiveDate',
                        label: 'в сила от',
                        render: (item) => formatDateBg(item?.anexEffectiveDate) || '-',
                    },
                    { key: 'fileName', label: 'от файл', render: (item) => dash(item?.fileName) },
                    { key: 'status', label: 'състояние', render: (item) => dash(item?.status) },
                    { key: 'noiCode', label: 'код корекция за НАП', render: (item) => dash(item?.noiCode) },
                    { key: 'nraStatus', label: 'НАП - състояние', render: (item) => dash(item?.nraStatus) },
                ],
            },
            {
                key: 'salary',
                label: 'Основното трудово възнаграждение',
                render: (item) => dash(item?.salary),
            },
        ],
        'dc-salary-change-from-file-report',
        '',
        {
            rowNumberLabel: '№ ред',
            tableFontSize: '5',
            titleFontSize: '10',
        },
    );

    return {
        ...baseConfig,
        compactTable: true,
        postTableLines: [
            'Подпис на ръководител:. . . . . . . . . . . . . . . . . . . . . . . . . . . . . .',
            '/Началник отдел "Кадри,протокол и информация" (Главен експерт "Човешки ресурси")/',
        ],
    };
}

function createEmployeeBankAccountsPdfConfig(asOfDate) {
    const dateText = formatDateBg(asOfDate || new Date());

    const baseConfig = createReportConfig(
        `Банкови сметки на служителите към дата ${dateText} г.`,
        [
            { key: 'empId', label: 'Сл.№', render: (item) => dash(item?.empId) },
            { key: 'idn', label: 'ЕГН/ЛНЧ', render: (item) => dash(item?.idn) },
            { key: 'idBdsk', label: 'ID', render: (item) => dash(item?.idBdsk) },
            { key: 'fullName', label: 'Име, Презиме, Фамилия', render: (item) => dash(item?.fullName) },
            { key: 'contractType', label: 'Вид тр.договор', render: (item) => dash(item?.contractType) },
            {
                label: 'Ниво в структурата',
                children: [
                    {
                        key: 'direction',
                        label: 'Направление',
                        render: (item) => dash(pickField(item, 'direction', 'directionName')),
                    },
                    {
                        key: 'structureLevelName',
                        label: 'Наименование',
                        render: (item) => dash(pickField(item, 'orgUnitName', 'structureLevelName', 'levelName', 'orgUnitLevelName')),
                    },
                    {
                        key: 'directorate',
                        label: 'Дирекция',
                        render: (item) => dash(pickField(item, 'directorate', 'directorateName')),
                    },
                    {
                        key: 'department',
                        label: 'Отдел',
                        render: (item) => dash(pickField(item, 'department', 'departmentName')),
                    },
                    {
                        key: 'division',
                        label: 'Звено',
                        render: (item) => dash(pickField(item, 'division', 'unit', 'unitName', 'zveno')),
                    },
                    {
                        key: 'group',
                        label: 'Група',
                        render: (item) => dash(pickField(item, 'group', 'groupName', 'grupa')),
                    },
                ],
            },
            {
                label: 'Банкова сметка',
                children: [
                    { key: 'bic', label: 'BIC', render: (item) => dash(item?.bic) },
                    { key: 'bankName', label: 'наименование на банков клон', render: (item) => dash(item?.bankName) },
                    { key: 'iban', label: 'IBAN', render: (item) => dash(item?.iban) },
                    {
                        key: 'bankAccStatus',
                        label: 'статус на сметката',
                        render: (item) => formatBankAccStatus(item?.bankAccStatus),
                    },
                    { key: 'note', label: 'забележка', render: (item) => dash(item?.note) },
                ],
            },
        ],
        'employee-bank-accounts-report',
        '',
        {
            rowNumberLabel: '№ ред',
            tableFontSize: '6',
            titleFontSize: '11',
        },
    );

    return {
        ...baseConfig,
        compactTable: true,
    };
}

function createPositionsHeldHistoryPdfConfig(fromDate, toDate) {
    const fromDateText = formatDateBg(fromDate || new Date());
    const toDateText = formatDateBg(toDate || new Date());

    return createReportConfig(
        `Справка за заеманите позиции от служителите в периода ${fromDateText} г. до ${toDateText} г.`,
        [
            {
                key: 'empOfficeName',
                label: 'Разходен център',
                render: (item) => dash(item?.empOfficeName),
            },
            {
                label: 'Служител',
                children: [
                    { key: 'empId', label: 'сл.№', render: (item) => dash(item?.empId) },
                    { key: 'fullName', label: 'име, презиме, фамилия', render: (item) => dash(item?.fullName) },
                    { key: 'orgUnitName', label: 'структурна група', render: (item) => dash(item?.orgUnitName) },
                    { key: 'positionNumber', label: 'код', render: (item) => dash(item?.positionNumber) },
                ],
            },
            {
                label: 'Заемана позиция',
                children: [
                    { key: 'positionName', label: 'наименование', render: (item) => dash(item?.positionName) },
                    {
                        key: 'fromDate',
                        label: 'от дата',
                        render: (item) => formatDateBg(item?.fromDate) || '-',
                    },
                    {
                        key: 'toDate',
                        label: 'до дата',
                        render: (item) => formatDateBg(item?.toDate) || '-',
                    },
                ],
            },
        ],
        'positions-held-history-report',
        '',
        { rowNumberLabel: '№ по ред' },
    );
}

function createPositionOccupantsHistoryPdfConfig(fromDate, toDate) {
    const fromDateText = formatDateBg(fromDate || new Date());
    const toDateText = formatDateBg(toDate || new Date());

    const baseConfig = createReportConfig(
        `Справка за история на промяна на позиция от дата ${fromDateText} г. до дата ${toDateText} г.`,
        [
            {
                key: 'unitLevelName',
                label: 'Код ниво',
                render: (item) => dash(pickField(item, 'unitLevelName', 'uniteLevelName')),
            },
            {
                label: 'Структурна единица',
                children: [
                    { key: 'orgUnitCode', label: 'Код', render: (item) => dash(item?.orgUnitCode) },
                    { key: 'orgUnitName', label: 'Наименование', render: (item) => dash(item?.orgUnitName) },
                ],
            },
            {
                label: 'Позиция',
                children: [
                    { key: 'positionCode', label: 'Код', render: (item) => dash(item?.positionCode) },
                    { key: 'positionName', label: 'Наименование', render: (item) => dash(item?.positionName) },
                ],
            },
            {
                label: 'Титуляр',
                children: [
                    { key: 'titularEmpId', label: 'Служебен Номер', render: (item) => dash(item?.titularEmpId) },
                    { key: 'titularEmpName', label: 'Име', render: (item) => dash(item?.titularEmpName) },
                ],
            },
            {
                label: 'Заемащ позицията',
                children: [
                    {
                        key: 'ocupantEmpId',
                        label: 'Служебен Номер',
                        render: (item) => dash(pickField(item, 'ocupantEmpId', 'occupantEmpId')),
                    },
                    {
                        key: 'ocupantEmpName',
                        label: 'Име',
                        render: (item) => dash(pickField(item, 'ocupantEmpName', 'occupantEmpName')),
                    },
                ],
            },
            {
                key: 'fromDate',
                label: 'От дата',
                render: (item) => formatDateBg(item?.fromDate) || '-',
            },
            {
                key: 'toDate',
                label: 'До дата',
                render: (item) => formatDateBg(item?.toDate) || '-',
            },
        ],
        'position-occupants-history-report',
        '',
        {
            rowNumberLabel: '№ ред',
            tableFontSize: '8',
            titleFontSize: '11',
        },
    );

    return {
        ...baseConfig,
        compactTable: true,
    };
}

function createFormNsiPdfConfig(asOfDate) {
    const date = asOfDate instanceof Date ? asOfDate : new Date(asOfDate);
    const currentYear = Number.isNaN(date.getTime()) ? new Date().getFullYear() : date.getFullYear();
    const previousYear = currentYear - 1;

    const baseConfig = createReportConfig(
        'Информация за персонала Приложение № 2',
        [],
        'form-nsi-report',
        '',
        {
            tableFontSize: '6',
            titleFontSize: '12',
        },
    );

    return {
        ...baseConfig,
        hidePageHeader: true,
        titleLines: ['Информация за персонала Приложение № 2'],
        titleAlignment: 'left',
        compactTable: true,
        buildCustomTable: (data, styles) => buildFormNsiCustomTable(
            data,
            styles,
            previousYear,
            currentYear,
        ),
    };
}

function createPersonnelMovementPdfConfig(toDate) {
    const toDateText = formatDateBg(toDate || new Date());
    const periodYear = (() => {
        const date = toDate instanceof Date ? toDate : new Date(toDate);
        return Number.isNaN(date.getTime()) ? new Date().getFullYear() : date.getFullYear();
    })();
    const countRender = (key) => (item) => formatMovementCount(item?.[key]);

    const baseConfig = createReportConfig(
        'И Н Ф О Р М А Ц И Я',
        [
            {
                key: 'personnelCategory',
                label: 'Категория персонал',
                render: (item) => dash(item?.personnelCategory),
            },
            {
                key: 'categoryCode',
                label: 'Шифър',
                render: (item) => formatMovementCount(item?.categoryCode),
            },
            {
                key: 'startHeadcount',
                label: 'Наличност към 01.01',
                render: countRender('startHeadcount'),
            },
            {
                key: 'hired',
                label: 'Постъпили през периода',
                render: countRender('hired'),
            },
            {
                key: 'terminated',
                label: 'Напуснали през периода',
                render: countRender('terminated'),
            },
            {
                key: 'currentHeadcount',
                label: 'Наличност към дата',
                render: countRender('currentHeadcount'),
            },
            {
                key: 'women',
                label: 'От тях жени',
                render: countRender('women'),
            },
            {
                label: `Разпределение на наличния персонал към ${toDateText} г. по:`,
                children: [
                    {
                        label: 'Образование',
                        children: [
                            { key: 'higherEducation', label: 'Висше', render: countRender('higherEducation') },
                            { key: 'secondaryEducation', label: 'Средно', render: countRender('secondaryEducation') },
                            { key: 'primaryEducation', label: 'Основно', render: countRender('primaryEducation') },
                        ],
                    },
                    {
                        label: 'Възраст',
                        children: [
                            { key: 'ageUnder30', label: 'до 30 години', render: countRender('ageUnder30') },
                            { key: 'age31to40', label: '31-40 години', render: countRender('age31to40') },
                            { key: 'age41to50', label: '41-50 години', render: countRender('age41to50') },
                            { key: 'ageOver50', label: 'над 50 години', render: countRender('ageOver50') },
                        ],
                    },
                    {
                        label: 'Трудов стаж',
                        children: [
                            { key: 'serviceUpTo9', label: 'до 9 години', render: countRender('serviceUpTo9') },
                            { key: 'service10to19', label: '10-19 години', render: countRender('service10to19') },
                            { key: 'service20Plus', label: 'над 20 години', render: countRender('service20Plus') },
                        ],
                    },
                    {
                        label: 'Трудов стаж в системата',
                        children: [
                            { key: 'companyServiceUpTo4', label: 'до 4 години', render: countRender('companyServiceUpTo4') },
                            { key: 'companyService5to9', label: '5-9 години', render: countRender('companyService5to9') },
                            { key: 'companyService10to15', label: '10-15 години', render: countRender('companyService10to15') },
                            { key: 'companyService15to19', label: '15-19 години', render: countRender('companyService15to19') },
                            { key: 'companyService20Plus', label: 'над 20 години', render: countRender('companyService20Plus') },
                        ],
                    },
                ],
            },
        ],
        'personnel-movement-report',
        '',
        {
            rowNumberLabel: '№ ред',
            tableFontSize: '5',
            titleFontSize: '12',
            subtitleFontSize: '10',
        },
    );

    return {
        ...baseConfig,
        hidePageHeader: true,
        titleLines: [
            'И Н Ф О Р М А Ц И Я',
            'за състава и движението на кадрите в банковата система на страната',
            `през ${periodYear} година`,
        ],
        compactTable: true,
    };
}

function createWomanSharePdfConfig(asOfDate) {
    const dateText = formatDateBg(asOfDate || new Date());

    return createReportConfig(
        `Относителен дял на жените към ${dateText}`,
        [
            {
                label: 'Структурна група',
                children: [
                    { key: 'code', label: 'код' },
                    { key: 'orgUnit', label: 'наименование' },
                ],
            },
            {
                label: 'Позиции',
                children: [
                    { key: 'totalPositions', label: 'по щат' },
                    { key: 'occupiedPositions', label: 'от тях заети' },
                    { key: 'occupiedByWomen', label: 'в т.ч жени' },
                ],
            },
            {
                key: 'womenPercentage',
                label: '% на жените към заетите бройки',
                render: (item) => formatWomenPercentage(item?.womenPercentage),
            },
        ],
        'ratio-women-personnel-report',
        '',
        { rowNumberLabel: '№ ред' },
    );
}

function createEmpCountPdfConfig(asOfDate) {
    const dateText = formatDateBg(asOfDate || new Date());

    return createReportConfig(
        `Численост на служителите по структурни групи към ${dateText}`,
        [
            {
                label: 'Структурна група',
                children: [
                    { key: 'code', label: 'код' },
                    { key: 'orgUnit', label: 'наименование' },
                ],
            },
            {
                label: 'Позиции',
                children: [
                    { key: 'totalPositions', label: 'по щат' },
                    { key: 'occupiedPositions', label: 'от тях заети' },
                ],
            },
            {
                key: 'averageAge',
                label: 'Средна възраст в години',
                render: (item) => {
                    const v = item?.averageAge;
                    if (v == null || v === '') return '-';
                    return String(v);
                },
            },
            {
                key: 'averageSalary',
                label: 'Средна основна заплата (евро)',
                render: (item) => formatDecimalBg(item?.averageSalary, 2),
            },
        ],
        'number-of-employees-report',
        '',
        { rowNumberLabel: '№ ред' },
    );
}

function createAverageMonthlySalaryPdfConfig(asOfDate) {
    const dateText = formatDateBg(asOfDate || new Date());

    return createReportConfig(
        `Информация за средната работна заплата към ${dateText}`,
        [
            {
                label: 'Категория персонал',
                children: [
                    {
                        key: 'code',
                        label: 'код',
                        render: (item) => item?.code ?? '-',
                    },
                    {
                        key: 'personnelCategory',
                        label: 'наименование',
                        render: (item) => item?.personnelCategory ?? '-',
                    },
                ],
            },
            {
                key: 'totalSalary',
                label: 'Работна заплата (евро)',
                render: (item) => formatDecimalBg(item?.totalSalary, 2),
            },
            {
                key: 'empCount',
                label: 'Брой персонал',
                render: (item) => {
                    const v = item?.empCount;
                    if (v == null || v === '') return '-';
                    return String(v);
                },
            },
            {
                key: 'averageSalary',
                label: 'Средна основна заплата (евро)',
                render: (item) => formatDecimalBg(item?.averageSalary, 2),
            },
            {
                key: 'averageAge',
                label: 'Средна възраст/години/',
                render: (item) => {
                    const v = item?.averageAge;
                    if (v == null || v === '') return '-';
                    return String(v);
                },
            },
        ],
        'average-monthly-salary-report',
        '',
        { rowNumberLabel: '№ ред' },
    );
}

function createContractTypeReportPdfConfig(asOfDate, contractType) {
    const dateText = formatDateBg(asOfDate || new Date());
    const isPermanent = contractType === CONTRACT_TYPE_PERMANENT;
    const title = isPermanent
        ? `Списък на служителите с постоянни трудови договори към ${dateText}`
        : `Списък на служителите със срочни трудови договори към ${dateText}`;

    const columns = [
        { key: 'empId', label: 'Сл.№' },
        { key: 'fullName', label: 'Име, Презиме, Фамилия' },
        {
            key: 'gender',
            label: 'Пол',
            render: (item) => formatGenderLabel(item?.gender),
        },
        { key: 'typeOfContract', label: 'Тр.договор' },
        { key: 'orgUnit', label: 'Структурна група' },
        { key: 'position', label: 'Длъжност' },
        { key: 'empContractsReason', label: 'Основание на трудовия договор' },
    ];

    if (!isPermanent) {
        columns.push({
            key: 'contractEndDate',
            label: 'Трудовият договор изтича на',
            render: (item) => formatContractExpiry(item),
        });
    }

    return createReportConfig(
        title,
        columns,
        contractType === CONTRACT_TYPE_PERMANENT
            ? 'employees-permanent-contracts-report'
            : 'employees-term-contracts-report',
        '',
        { rowNumberLabel: '№ ред' },
    );
}

async function getContractTypeReport(payload) {
    const response = await api.post('/emp-report/contract-type', payload);
    return response.data;
}

async function getWomanShareReport(payload) {
    const response = await api.post('/emp-report/woman-share', payload);
    return toSingleRowReportData(response.data);
}

async function getEmpCountReport(payload) {
    const response = await api.post('/emp-report/emp-count', payload);
    return toSingleRowReportData(response.data);
}

async function getAverageMonthlySalaryReport(payload) {
    const response = await api.post('/emp-report/avg-salary', payload);
    return toSingleRowReportData(response.data);
}

async function getEmpListReport(payload) {
    const response = await api.post('/emp-report/emp-list', payload);
    const data = response.data;
    return Array.isArray(data) ? data : [];
}

async function getEmpExpReport(payload) {
    const response = await api.post('/emp-report/emp-exp', payload);
    return normalizeToArray(response.data);
}

async function getTerminatedEmployeesReport(payload) {
    const response = await api.post('/emp-report/emp-term', payload);
    return normalizeToArray(response.data);
}

async function getAppointedEmployeesReport(payload) {
    const response = await api.post('/emp-report/emp-appointed', payload);
    return normalizeToArray(response.data);
}

async function getEmpDisabilityReport(payload) {
    const response = await api.post('/emp-report/emp-disability', payload);
    return normalizeToArray(response.data);
}

async function getNominalStaffReport(payload) {
    const response = await api.post('/emp-report/nominal-staff', payload);
    return normalizeToArray(response.data);
}

async function getPositionStaffReport(payload) {
    const response = await api.post('/emp-report/position-staff', payload);
    return normalizeToArray(response.data);
}

async function getEmpMovementReport(payload) {
    const response = await api.post('/emp-report/emp-movement', payload);
    return normalizeToArray(response.data);
}

async function getNssiInfoReport(payload) {
    const response = await api.post('/emp-report/nssi-info', payload);
    return normalizeToArray(response.data);
}

async function getOrgUnitHistoryReport(payload) {
    const response = await api.post('/emp-report/org-unit-history', payload);
    return normalizeToArray(response.data);
}

async function getPositionCostCenterHistoryReport(payload) {
    const response = await api.post('/emp-report/position-cost-center-history', payload);
    return normalizeToArray(response.data);
}

async function getPositionOccupationHistoryReport(payload) {
    const response = await api.post('/emp-report/position-ocupation-history', payload);
    return normalizeToArray(response.data);
}

async function getOccupationHistPositionsReport(payload) {
    const response = await api.post('/emp-report/ocupation-hist-positions', payload);
    return normalizeToArray(response.data);
}

async function getKindInsuranceReport(payload) {
    const response = await api.post('/emp-report/kind-insurance', payload);
    return normalizeToArray(response.data);
}

async function getEmployeeChildrenReport(payload) {
    const response = await api.post('/emp-report/child', payload);
    return normalizeToArray(response.data);
}

async function getBankAccReport(payload) {
    const response = await api.post('/emp-report/bank-acc', payload);
    return normalizeToArray(response.data);
}

async function getSalaryChangeReport(payload) {
    const response = await api.post('/emp-report/salary-change', payload);
    return normalizeToArray(response.data);
}

async function getPositionRightsReport(payload) {
    const response = await api.post('/emp-report/position-rights', payload);
    return normalizeToArray(response.data);
}

export function buildHrInformationReportExecution(reportKey, formState) {
    if (reportKey === AGE_REPORT_KEY) {
        const payload = buildAgeReportPayload(formState);
        return {
            reportService: () => getAgeReport(payload),
            reportConfig: createAgeReportConfigWithCriteria(formState.forDate, payload),
        };
    }

    const contractType = CONTRACT_TYPE_BY_REPORT_KEY[reportKey];
    if (contractType) {
        const payload = buildContractTypeReportPayload(formState, contractType);
        return {
            reportService: () => getContractTypeReport(payload),
            reportConfig: createContractTypeReportPdfConfig(formState.forDate, contractType),
        };
    }

    if (reportKey === WOMAN_SHARE_REPORT_KEY) {
        const payload = buildDateOrgUnitReportPayload(formState);
        return {
            reportService: () => getWomanShareReport(payload),
            reportConfig: createWomanSharePdfConfig(formState.forDate),
        };
    }

    if (reportKey === EMP_COUNT_REPORT_KEY) {
        const payload = buildDateOrgUnitReportPayload(formState);
        return {
            reportService: () => getEmpCountReport(payload),
            reportConfig: createEmpCountPdfConfig(formState.forDate),
        };
    }

    if (reportKey === AVERAGE_MONTHLY_SALARY_REPORT_KEY) {
        const payload = buildDateOrgUnitReportPayload(formState);
        return {
            reportService: () => getAverageMonthlySalaryReport(payload),
            reportConfig: createAverageMonthlySalaryPdfConfig(formState.forDate),
        };
    }

    if (reportKey === EMPLOYEE_LIST_REPORT_KEY) {
        const payload = buildEmpListReportPayload(formState);
        return {
            reportService: () => getEmpListReport(payload),
            reportConfig: createEmpListPdfConfig(formState.forDate),
        };
    }

    if (reportKey === EMPLOYEE_LENGTH_OF_SERVICE_REPORT_KEY) {
        const payload = buildEmpExpReportPayload(formState);
        return {
            reportService: () => getEmpExpReport(payload),
            reportConfig: createEmpExpPdfConfig(formState.forDate),
        };
    }

    if (reportKey === TERMINATED_EMPLOYEES_REPORT_KEY) {
        const payload = buildPeriodOrgUnitReportPayload(formState);
        return {
            reportService: () => getTerminatedEmployeesReport(payload),
            reportConfig: createTerminatedEmployeesPdfConfig(formState.fromDate, formState.toDate),
        };
    }

    if (reportKey === APPOINTED_EMPLOYEES_REPORT_KEY) {
        const payload = buildPeriodOrgUnitReportPayload(formState);
        return {
            reportService: () => getAppointedEmployeesReport(payload),
            reportConfig: createAppointedEmployeesPdfConfig(formState.fromDate, formState.toDate),
        };
    }

    if (reportKey === REDUCED_WORKING_CAPACITY_REPORT_KEY) {
        const payload = buildPeriodOrgUnitReportPayload(formState);
        return {
            reportService: () => getEmpDisabilityReport(payload),
            reportConfig: createReducedWorkingCapacityPdfConfig(formState.fromDate, formState.toDate),
        };
    }

    if (reportKey === PERSONNEL_TABLE_REPORT_KEY) {
        const payload = buildDateOrgUnitReportPayload(formState);
        return {
            reportService: () => getNominalStaffReport(payload),
            reportConfig: createPersonnelTablePdfConfig(formState.forDate),
        };
    }

    if (reportKey === JOB_TABLE_REPORT_KEY) {
        const payload = buildDateOrgUnitReportPayload(formState);
        return {
            reportService: () => getPositionStaffReport(payload),
            reportConfig: createJobTablePdfConfig(formState.forDate),
        };
    }

    if (reportKey === PERSONNEL_MOVEMENT_REPORT_KEY) {
        const payload = buildPeriodOrgUnitReportPayload(formState);
        return {
            reportService: () => getEmpMovementReport(payload),
            reportConfig: createPersonnelMovementPdfConfig(formState.toDate),
        };
    }

    if (reportKey === FORM_NSI_REPORT_KEY) {
        const payload = buildDateOrgUnitReportPayload(formState);
        return {
            reportService: () => getNssiInfoReport(payload),
            reportConfig: createFormNsiPdfConfig(formState.forDate),
        };
    }

    if (reportKey === STRUCTURAL_GROUP_HISTORY_REPORT_KEY) {
        const payload = buildRequiredOrgUnitPayload(formState);
        return {
            reportService: () => getOrgUnitHistoryReport(payload),
            reportConfig: createStructuralGroupHistoryPdfConfig(formState.fromDate, formState.toDate),
        };
    }

    if (reportKey === COST_CENTER_HISTORY_REPORT_KEY) {
        const payload = buildRequiredPositionPayload(formState);
        return {
            reportService: () => getPositionCostCenterHistoryReport(payload),
            reportConfig: createCostCenterHistoryPdfConfig(formState.fromDate, formState.toDate),
        };
    }

    if (reportKey === POSITION_OCCUPANTS_HISTORY_REPORT_KEY) {
        const payload = buildRequiredPositionPayload(formState);
        return {
            reportService: () => getPositionOccupationHistoryReport(payload),
            reportConfig: createPositionOccupantsHistoryPdfConfig(formState.fromDate, formState.toDate),
        };
    }

    if (reportKey === RIGHTS_OF_POSITION_REPORT_KEY) {
        const payload = buildPositionRightsReportPayload(formState);
        return {
            reportService: async () => {
                const [rightKeys, reportData] = await Promise.all([
                    structuralGroupService.getCheckboxPositionRights(),
                    getPositionRightsReport(payload),
                ]);
                return {
                    reportData,
                    reportConfig: createPositionRightsPdfConfig(formState.forDate, rightKeys),
                };
            },
            reportConfig: null,
        };
    }

    if (reportKey === POSITIONS_HELD_HISTORY_REPORT_KEY) {
        const payload = buildPeriodOrgUnitReportPayload(formState);
        return {
            reportService: () => getOccupationHistPositionsReport(payload),
            reportConfig: createPositionsHeldHistoryPdfConfig(formState.fromDate, formState.toDate),
        };
    }

    if (reportKey === EMPLOYEES_INSURANCE_TYPE_REPORT_KEY) {
        const payload = buildDateOrgUnitReportPayload(formState);
        return {
            reportService: () => getKindInsuranceReport(payload),
            reportConfig: createEmployeesInsuranceTypePdfConfig(formState.forDate),
        };
    }

    if (reportKey === EMPLOYEE_CHILDREN_UNDER_19_REPORT_KEY) {
        const payload = buildDateOrgUnitReportPayload(formState);
        return {
            reportService: () => getEmployeeChildrenReport(payload),
            reportConfig: createEmployeeChildrenUnder19PdfConfig(formState.forDate),
        };
    }

    if (reportKey === DC_SALARY_CHANGE_FROM_FILE_REPORT_KEY) {
        const payload = buildPeriodOrgUnitReportPayload(formState);
        return {
            reportService: () => getSalaryChangeReport(payload),
            reportConfig: createDcSalaryChangeFromFilePdfConfig(),
        };
    }

    if (reportKey === EMPLOYEE_BANK_ACCOUNTS_REPORT_KEY) {
        const payload = buildDateOrgUnitReportPayload(formState);
        return {
            reportService: () => getBankAccReport(payload),
            reportConfig: createEmployeeBankAccountsPdfConfig(formState.forDate),
        };
    }

    throw new Error('Selected report is not implemented yet.');
}

